import type { Category, CategoryId } from '../features/catalog/model';
import { m } from '../paraglide/messages';

type Label = (inputs?: Record<string, never>, options?: { locale?: 'en' }) => string;

/** Each category's display name. `CATS` keeps the English one, which the
 *  catalog also searches and filters by; `categoryLabel` is the one to show. */
const LABEL: Record<CategoryId, Label> = {
  all: m.category_all,
  social: m.category_social,
  search: m.category_search,
  enrichment: m.category_enrichment,
  data: m.category_data,
  video: m.category_video,
  shop: m.category_shop,
  travel: m.category_travel,
  fin: m.category_fin,
  music: m.category_music,
};

const english = (id: CategoryId): string => LABEL[id]({}, { locale: 'en' });

/** A category's name in the locale of the render. */
export const categoryLabel = (id: CategoryId): string => LABEL[id]();

/**
 * Catalog categories in browse order - the one owner of it, read by the sidebar
 * and by the catalog's category sections alike.
 *
 * Ordered by measured 30d demand, with one deliberate exception: Enrichment is
 * pinned above Knowledge & Data because it is a young family that no one can
 * find yet, so its traffic reflects merchandising rather than interest. That
 * pin is an editorial call. Everything below it is not.
 */
export const CATS: Category[] = [
  { id: 'all', name: english('all'), icon: 'layout-grid', blurb: 'every API we serve' },
  { id: 'social', name: english('social'), icon: 'at-sign', blurb: 'profiles, posts, comments and search, straight from the feeds' },
  { id: 'search', name: english('search'), icon: 'search', blurb: 'search results, rankings, keywords, ads and any web page as clean data' },
  { id: 'enrichment', name: english('enrichment'), icon: 'sparkles', blurb: 'people and company records, emails and phone numbers, from a name, email or domain' },
  { id: 'data', name: english('data'), icon: 'database', blurb: 'code, maps, homes, jobs and salaries from the sites that hold them' },
  { id: 'video', name: english('video'), icon: 'play', blurb: 'videos, channels, comments and transcripts' },
  { id: 'shop', name: english('shop'), icon: 'shopping-cart', blurb: 'listings, prices, sellers and reviews across the marketplaces' },
  { id: 'travel', name: english('travel'), icon: 'plane', blurb: 'hotels, flights, stays and traveler reviews' },
  { id: 'fin', name: english('fin'), icon: 'trending-up', blurb: 'prices, markets, filings and on-chain data' },
  { id: 'music', name: english('music'), icon: 'music', blurb: 'tracks, artists, playlists and charts' },
];

/** Per-category accent color tokens - verbatim from catalog.html. */
export const CCOLOR: Record<CategoryId, string> = {
  all: 'var(--accent)',
  enrichment: 'var(--accent)',
  social: 'var(--c-violet)',
  music: 'var(--c-pink)',
  video: 'var(--c-amber)',
  shop: 'var(--c-green)',
  search: 'var(--c-blue)',
  fin: 'var(--c-teal)',
  travel: 'var(--c-slate)',
  data: 'var(--c-slate)',
};

/** Map of category id → display name. */
export const CNAME: Record<CategoryId, string> = CATS.reduce(
  (acc, c) => {
    acc[c.id] = c.name;
    return acc;
  },
  {} as Record<CategoryId, string>,
);
